import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody, CardHeader, Col, Spinner } from "reactstrap";
import { getActivityHeatmap } from "common/services/analytics.service";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const formatHour = (hour: number) => {
  if (hour === 0) return "12am";
  if (hour === 12) return "12pm";
  return hour < 12 ? `${hour}am` : `${hour - 12}pm`;
};

/**
 * Visits bucketed by weekday and hour (UTC), rendered as a density grid.
 * Shows when the system is actually used, which the trend line cannot.
 */
const ActivityHeatmap = ({ typeOfRequest }: { typeOfRequest: string }) => {
  const [grid, setGrid] = useState<number[][]>([]);
  const [peak, setPeak] = useState<any>(null);
  const [total, setTotal] = useState<number>(0);
  const [businessHoursShare, setBusinessHoursShare] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const response: any = await getActivityHeatmap({ typeOfRequest });
        if (cancelled) return;
        setGrid(response?.data?.grid || []);
        setPeak(response?.data?.peak || null);
        setTotal(response?.data?.total || 0);
        setBusinessHoursShare(response?.data?.businessHoursShare || 0);
      } catch (e) {
        if (!cancelled) setGrid([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [typeOfRequest]);

  const max = useMemo(
    () => grid.reduce((m, row) => Math.max(m, ...row), 0),
    [grid]
  );

  // Five opacity steps keep quiet-but-nonzero hours visible against empty ones.
  const cellStyle = (value: number) => {
    if (!value) {
      return {
        backgroundColor: "var(--vz-light)",
        opacity: 0.5,
      };
    }
    const ratio = max ? value / max : 0;
    const step = Math.max(1, Math.ceil(ratio * 5));
    return {
      backgroundColor: "var(--vz-primary)",
      opacity: 0.15 + step * 0.17,
    };
  };

  return (
    <Col xxl={12}>
      <Card className="card-height-100">
        <CardHeader className="align-items-center d-flex">
          <h4 className="card-title mb-0 flex-grow-1">
            When the System Is Used
          </h4>
          {!loading && total > 0 && (
            <span className="badge bg-primary-subtle text-primary fs-12">
              {businessHoursShare}% in Mon&ndash;Fri work hours
            </span>
          )}
        </CardHeader>
        <CardBody>
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" size="sm" />
            </div>
          ) : total === 0 ? (
            <div className="text-center text-muted py-5">
              No activity recorded for this period.
            </div>
          ) : (
            <React.Fragment>
              <div className="table-responsive">
                <table
                  className="mb-0"
                  style={{ borderCollapse: "separate", borderSpacing: "2px" }}
                >
                  <thead>
                    <tr>
                      <th></th>
                      {Array.from({ length: 24 }).map((_, hour) => (
                        <th
                          key={hour}
                          className="text-muted fw-normal text-center"
                          style={{ fontSize: "9px", minWidth: "18px" }}
                        >
                          {hour % 3 === 0 ? hour : ""}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {DAY_LABELS.map((label, day) => (
                      <tr key={label}>
                        <td
                          className="text-muted fw-medium pe-2"
                          style={{ fontSize: "11px", whiteSpace: "nowrap" }}
                        >
                          {label}
                        </td>
                        {Array.from({ length: 24 }).map((_, hour) => {
                          const value = grid?.[day]?.[hour] || 0;
                          return (
                            <td
                              key={hour}
                              title={`${label} ${formatHour(hour)} UTC — ${value} visit${
                                value === 1 ? "" : "s"
                              }`}
                              style={{
                                ...cellStyle(value),
                                height: "20px",
                                minWidth: "18px",
                                borderRadius: "3px",
                              }}
                            ></td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="d-flex align-items-center justify-content-between mt-3 flex-wrap gap-2">
                {peak && peak.visits > 0 && (
                  <p className="text-muted mb-0 fs-13">
                    Busiest:{" "}
                    <span className="fw-medium text-body">
                      {DAY_LABELS[peak.dayOfWeek]} at{" "}
                      {formatHour(peak.hourOfDay)} UTC
                    </span>{" "}
                    ({peak.visits} visits)
                  </p>
                )}
                <div className="d-flex align-items-center gap-1">
                  <span className="text-muted fs-11 me-1">Less</span>
                  {[0, 1, 2, 3, 4, 5].map((step) => (
                    <span
                      key={step}
                      style={{
                        display: "inline-block",
                        width: "14px",
                        height: "14px",
                        borderRadius: "3px",
                        backgroundColor: step
                          ? "var(--vz-primary)"
                          : "var(--vz-light)",
                        opacity: step ? 0.15 + step * 0.17 : 0.5,
                      }}
                    ></span>
                  ))}
                  <span className="text-muted fs-11 ms-1">More</span>
                </div>
              </div>
            </React.Fragment>
          )}
        </CardBody>
      </Card>
    </Col>
  );
};

export default ActivityHeatmap;
